import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { format } from "date-fns";
import { mapRichTextToHtml } from "@repo/utils";
import { Workbook } from "@protobi/exceljs";
import { HELP_EMAIL, universalDateParser } from "@utils";
import { useXlsImporterSettings } from "@tools";
import { Alert } from "@components";
import { Grid, LinearProgress, Link } from "@material-ui/core";
import { XlsFile } from "@assets";
import useStyle from "./Steps.style";

export const Import = () => {
  const classes = useStyle();
  const { t } = useTranslation();
  const settings = useXlsImporterSettings();

  const [ isLoading, setLoading ] = useState(false);
  const [ alertVisible, showAlert ] = useState(false);
  const [ alertText, setAlertText ] = useState("");
  const [ alertSubText, setAlertSubText ] = useState("");
  const [ progress, setProgress ] = useState(0);

  const handleAlertClose = () => {
    showAlert(false);
    setAlertText("");
    setAlertSubText("");
  }
  
  const fileGetter = async (fileList) => {
    const files = [];
    setProgress(0);
    setLoading(true);

    if (fileList || fileList.length) {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        files.push(file);
      }
    }

    const reader = new window.FileReader();
    reader.onload = async () => {
      try {
        const wb = new Workbook();
        await wb.xlsx.load(reader.result, { ignoreNodes: ["picture", "drawing"] });
        
        const htmlColumns = [];
        
        settings.initXlsData(
          wb.worksheets
            .filter((worksheet) => worksheet.columns)
            .map((worksheet) => {
              // worksheet._columns = worksheet._columns.slice(0, 100);
              
              const lastFilledColumn = worksheet.columns.slice(0,100).findLastIndex(c => c.values.length && c.values.find(v => v)) + 1
              
              worksheet._columns = worksheet._columns.slice(0, lastFilledColumn);
              // worksheet.spliceColumns(lastFilledColumn, worksheet.columns.length-lastFilledColumn);
              
              const dimensions = [
                Math.min(worksheet.dimensions.right, lastFilledColumn),
                worksheet.dimensions.bottom,
              ];
              
              let data = [...new Array(dimensions[1])].map(() =>
                [...new Array(dimensions[0])].map(() => "")
              );
              
              worksheet.eachRow((row, rowNumber) => {
                if (!row) {
                  return;
                }
                if (rowNumber <= dimensions[1]) {
                  for(let i=0; i < dimensions[0]; i++) {
                    const cell = row._cells[i];
                    if(!cell)
                      continue;
                    
                    const colNumber = i + 1;
                    // if (colNumber <= dimensions[0]) {
                    let v = "";
                    if (cell?.value || cell?.value === 0) {
                      if (typeof cell.value === "object") {
                        if (cell.value instanceof Date) {
                          v = format(
                            universalDateParser(cell.value),
                            cell?.numFmt
                              ?.replace("mm", "MM")
                              .replace("D", "d")
                              .replace("Y", "y")
                            || "dd.MM.yyyy"
                          );
                        }
                        else if(cell.value.richText || cell.value.text?.richText) {
                          if(!htmlColumns.includes(colNumber))
                            htmlColumns.push(colNumber)
                          v = mapRichTextToHtml(cell.value.richText || cell.value.text.richText, cell.value.hyperlink)
                        }
                        else
                          v =
                            cell.result === "#REF!" ||
                            cell.value.result === "#REF!"
                              ? ""
                              : cell.result || cell.value.result;
                      } else if(typeof cell.value === "string" && isNaN(Number(cell.value)) && cell.style?.font) {
                        if(!htmlColumns.includes(colNumber))
                          htmlColumns.push(colNumber)
                        v = mapRichTextToHtml([
                          { text: cell.value, ...cell.style }
                        ])
                      } else v = cell.value;
                    }
                    
                    if (typeof v === "number")
                      v = parseFloat(v.toPrecision(12));
                    
                    if (
                      cell?.numFmt &&
                      cell.numFmt.match(/(0.?0*$)/g) &&
                      cell?.value
                    ) {
                      const p = Math.pow(
                        10,
                        (cell.numFmt + ".").split(".")[1].length
                      );
                      v = Math.round(v * p) / p;
                    }
                    
                    data[rowNumber - 1][colNumber - 1] = v ? v.toString() : "";
                    // }
                  }
                }
              });
              
              return {
                title: worksheet.name,
                parsed: false,
                // used: i===0, //wb.worksheets.length === 1,
                data,
                colWidths: worksheet.columns
                  .map((c) => (c?.width ? c?.width * 8 : 0)),
                columns: worksheet.columns
                  .map((_, i) => htmlColumns.includes(i+1)
                    ? { type: "html" }
                    : { type: "text" }
                  ),
                mergeCells: Object.keys(worksheet._merges)
                  .filter(key => worksheet._merges[key].left < dimensions[0])
                  .reduce((p, key) => {
                    const { left, right, top, bottom } = worksheet._merges[key];
                    return { ...p, [key]: [Math.min(right, dimensions[0]) - left + 1, Math.min(bottom, dimensions[1]) - top + 1] };
                  }, {}),
                minDimensions: dimensions,
              };
            }),
          fileList[0].name
        );
      } catch(e) {
        console.error(e);
        setAlertText(t("views.import.xls_error.title"));
        setAlertSubText(<p>
          {t("views.import.xls_error.subtitle")}
          <Link href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</Link>
        </p>);
        showAlert(true);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(files[0]);
    reader.onprogress = (p) => {
      setProgress(Math.min((p.loaded / p.total) * 100, 100));
    };
    reader.onerror = (e) => {
      console.error(e);
      const code = e?.[0]?.errors[0]?.code;
      if(code)
        setAlertText(`errors.dropzone.${code}`);
      showAlert(true);
      setLoading(false);
    };
  };

  const handleFileReject = (reason) => {
    console.error(reason);
    const code = reason?.[0]?.errors[0]?.code;
    if(code)
      setAlertText(`errors.dropzone.${code}`);
    showAlert(true);
    setLoading(false);
  }

  const { getInputProps, getRootProps, open, isDragActive } = useDropzone({
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
    noClick: true,
    noKeyboard: true,
    multiple: false,
    onDropAccepted: fileGetter,
    onDropRejected: handleFileReject,
  });

  return (
    <Grid item container direction="column" className="overflow-hidden">
      <Grid
        item
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        {...getRootProps()}
        onClick={open}
        className={classes.dropzone}
      >
        <div className={classes.iconBackground}>
          <XlsFile style={{ fontSize: 60 }} />
        </div>
        {isLoading ? (
          <LinearProgress value={progress} variant="determinate" />
        ) : (
          <p className="font-bold">
            {isDragActive
              ? t("views.import.drop_active")
              : t("views.import.drop_inactive")}
          </p>
        )}
        {!isLoading && !isDragActive && (
          <p className="transparent-3 mt-1 ">
            {t("views.import.drop_inactive_sub")}
          </p>
        )}
        <input
          {...getInputProps()}
          className="display-block h-0 w-full"
          tabIndex="0"
          aria-label={t("views.import.drop_inactive")}
        />
      </Grid>
      <Alert
        title={alertText ? `${t("views.import.import_error")} - ${t(alertText)}` : t("views.import.import_error")}
        isOpen={alertVisible}
        onAccept={handleAlertClose}
        acceptText={t("common.close")}
        width={420}
      >
        { alertSubText }
      </Alert>
    </Grid>
  );
};
